/**
 * <bk-endpoint-search> — the developer endpoint search (markup: sections/developer-search.liquid).
 *
 * The index is inline in the page (members only, never in an asset):
 *   <script type="application/json" data-search-index>{"r":[[name,url,category,description],…],"e":[[r,method,path,summary,anchor],…]}</script>
 *
 * Behaviour = the design's renderVals() (docs/design/pages/search):
 * - needle = query trimmed + lower-cased; a row matches when "path summary resource" contains
 *   every word of it, in any order ("create vendor" finds "Create a new vendor.");
 * - resources whose name, description or category contain it are listed first ("Matching APIs"),
 *   so a word that is only in a resource's description ("supplier" → Vendors) still finds it;
 * - method pills count the text matches; a method with no matches is disabled unless pressed;
 * - hits are grouped by resource in index order (category → resource → YAML order), each group
 *   headed by the resource link and "<category> · N matches";
 * - the first occurrence of the needle in the path and in the summary is wrapped in <mark>;
 * - summary "N endpoints in N resources match “q”"; no matches → the "Nothing matches" panel;
 *   empty query → the two tip cards.
 * The query is read from ?q= on load and written back with history.replaceState while typing
 * (debounced). Large result sets render the first MAX_ROWS hits plus a "Show N more" button.
 */
const METHODS = ['ALL', 'GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const DEBOUNCE_MS = 120;
const MAX_ROWS = 200;

const escapeHtml = (text) =>
  String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/** Query → words; a row matches when it contains every word, in any order ("create vendor"). */
const wordsOf = (needle) => needle.split(/\s+/).filter(Boolean);
const hasAll = (hay, words) => words.every((w) => hay.indexOf(w) !== -1);

const fill = (template, values) => template.replace(/\[(\w+)\]/g, (whole, key) => (key in values ? values[key] : whole));

/** Design's split(): highlight the first occurrence of the needle (lowerText is text lower-cased). */
function highlight(text, lowerText, needle) {
  // Whole query if it appears as typed; otherwise its first word that does.
  if (needle && lowerText.indexOf(needle) === -1) needle = wordsOf(needle).find((w) => lowerText.indexOf(w) !== -1) || needle;
  const i = needle ? lowerText.indexOf(needle) : -1;
  if (i === -1) return escapeHtml(text);
  return escapeHtml(text.slice(0, i)) + '<mark>' + escapeHtml(text.slice(i, i + needle.length)) + '</mark>' + escapeHtml(text.slice(i + needle.length));
}

class BkEndpointSearch extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;

    let data = { r: [], e: [] };
    try {
      const source = this.querySelector('script[data-search-index]');
      if (source) data = JSON.parse(source.textContent);
    } catch (error) {
      /* Malformed index: search finds nothing rather than breaking the page. */
    }
    this.resources = data.r || [];
    this.resourceHay = this.resources.map(([name, , , description]) => [name, description].filter(Boolean).join(' ').toLowerCase());
    this.rows = (data.e || []).map(([r, method, path, summary, anchor]) => {
      const pathLower = String(path).toLowerCase();
      const summaryLower = String(summary).toLowerCase();
      const resourceLower = String((data.r[r] || [])[0] || '').toLowerCase();
      return { r, method, path, summary, anchor, pathLower, summaryLower, hay: pathLower + ' ' + summaryLower + ' ' + resourceLower };
    });

    this.method = 'ALL';
    this.limit = MAX_ROWS;
    this.form = this.querySelector('form[role="search"]');
    this.input = this.querySelector('#search-q');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.results = this.querySelector('[data-search-results]');
    this.summary = this.querySelector('#search-count');
    this.groupsEl = this.querySelector('[data-search-groups]');
    this.none = this.querySelector('[data-search-none]');
    this.noneHeading = this.querySelector('[data-search-none-heading]');
    this.tips = this.querySelector('[data-search-tips]');
    this.pills = Array.from(this.querySelectorAll('.bk-pill[data-method]'));

    const initial = new URLSearchParams(window.location.search).get('q') || '';
    if (this.input) {
      if (initial) this.input.value = initial;
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.update(true), DEBOUNCE_MS);
      });
    }
    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        clearTimeout(this.timer);
        this.update(true);
      });
    }
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        if (this.input) this.input.value = '';
        this.method = 'ALL';
        this.update(true);
        if (this.input) this.input.focus();
      });
    }
    this.pills.forEach((pill) => {
      pill.addEventListener('click', () => {
        this.method = pill.dataset.method;
        this.update(false);
      });
    });
    if (this.groupsEl) {
      this.groupsEl.addEventListener('click', (event) => {
        const more = event.target.closest('[data-search-more]');
        if (!more) return;
        const firstNew = this.limit;
        this.limit = Infinity;
        this.render();
        const next = this.groupsEl.querySelectorAll('.bk-hit')[firstNew];
        if (next) next.focus();
      });
    }

    this.update(false);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
  }

  /** Recompute matches for the current query. `syncUrl` writes ?q= back to the address bar. */
  update(syncUrl) {
    const q = this.input ? this.input.value : '';
    const needle = q.trim().toLowerCase();
    if (needle !== this.needle) this.limit = MAX_ROWS;
    this.q = q;
    this.needle = needle;
    const words = wordsOf(needle);
    this.textMatched = needle ? this.rows.filter((row) => hasAll(row.hay, words)) : [];
    // An API is listed when its own name or description has every word (not just its category's name).
    this.resourceMatched = needle ? this.resources.map((r, i) => i).filter((i) => hasAll(this.resourceHay[i], words)) : [];
    if (syncUrl) this.syncUrl(q);
    this.render();
  }

  syncUrl(q) {
    try {
      const url = new URL(window.location.href);
      if (q.trim()) url.searchParams.set('q', q);
      else url.searchParams.delete('q');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    } catch (error) {
      /* replaceState unavailable (e.g. sandboxed) — the search still works. */
    }
  }

  plural(n, key) {
    const d = this.dataset;
    return fill(n === 1 ? d[key + 'Singular'] : d[key + 'Plural'], { count: String(n) });
  }

  render() {
    const needle = this.needle;
    const hasQuery = needle !== '';
    const method = this.method;
    const matched = this.textMatched;
    const visible = method === 'ALL' ? matched : matched.filter((row) => row.method === method);

    if (this.clearButton) this.clearButton.hidden = !hasQuery;
    if (this.tips) this.tips.hidden = hasQuery;
    if (this.results) this.results.hidden = !hasQuery;
    if (!hasQuery) {
      if (this.groupsEl) this.groupsEl.innerHTML = '';
      if (this.summary) this.summary.textContent = '';
      return;
    }

    const counts = { ALL: matched.length };
    for (const row of matched) counts[row.method] = (counts[row.method] || 0) + 1;
    for (const pill of this.pills) {
      const m = pill.dataset.method;
      const count = counts[m] || 0;
      pill.setAttribute('aria-pressed', m === method ? 'true' : 'false');
      pill.disabled = m !== 'ALL' && count === 0 && m !== method;
      const n = pill.querySelector('.bk-pill-n');
      if (n) n.textContent = String(count);
    }

    // Group by resource, in index order (rows are already category → resource ordered).
    const groups = [];
    const byResource = new Map();
    for (const row of visible) {
      let group = byResource.get(row.r);
      if (!group) {
        group = { r: row.r, hits: [] };
        byResource.set(row.r, group);
        groups.push(group);
      }
      group.hits.push(row);
    }

    const query = this.q.trim();
    if (this.summary) {
      this.summary.textContent = fill(this.dataset.summary, {
        endpoints: this.plural(visible.length, 'endpoint'),
        resources: this.plural(groups.length, 'resource'),
        query
      });
    }

    const apis = this.resourceMatched || [];
    const nothing = visible.length === 0 && apis.length === 0;
    if (this.none) this.none.hidden = !nothing;
    const methodTip = this.querySelector('[data-search-method-tip]');
    if (methodTip) methodTip.hidden = method === 'ALL';
    if (this.noneHeading && nothing) {
      this.noneHeading.textContent = fill(this.dataset.noResults, {
        query,
        method: method === 'ALL' ? '' : fill(this.dataset.methodNote, { method })
      });
    }

    let budget = this.limit;
    let html = '';
    if (apis.length) {
      html +=
        '<section aria-labelledby="grp-apis" style="display: flex; flex-direction: column; gap: 12px;">' +
        '<h2 id="grp-apis" style="margin: 0; font-size: 20px; font-weight: 700; line-height: 1.35;">' + escapeHtml(this.dataset.resourcesHeading || 'Matching APIs') + '</h2>' +
        '<ul role="list" style="list-style: none; margin: 0; padding: 0; border-top: 1px solid var(--rule);">';
      for (const i of apis) {
        const [name, url, category, description] = this.resources[i];
        html +=
          '<li><a class="bk-hit bk-hit-api" href="' + escapeHtml(url) + '">' +
          '<span style="font-size: 16px; font-weight: 700; line-height: 1.4; color: var(--heading);">' + highlight(name, name.toLowerCase(), needle) + '</span>' +
          '<span class="bk-hit-sum" style="font-size: 15px; line-height: 1.5; color: var(--text);">' + highlight(description || '', (description || '').toLowerCase(), needle) +
          ' <span style="color: var(--text-secondary);">· ' + escapeHtml(category) + '</span></span>' +
          '</a></li>';
      }
      html += '</ul></section>';
    }
    for (const group of groups) {
      if (budget <= 0) break;
      const [name, url, category] = this.resources[group.r] || ['', '', ''];
      const hid = 'grp-' + group.r;
      const shown = group.hits.slice(0, budget);
      budget -= shown.length;
      html +=
        '<section aria-labelledby="' + hid + '" style="display: flex; flex-direction: column; gap: 12px;">' +
        '<div style="display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 16px;">' +
        '<h2 id="' + hid + '" style="margin: 0; font-size: 20px; font-weight: 700; line-height: 1.35;"><a class="bk-grp" href="' + escapeHtml(url) + '">' + escapeHtml(name) + '</a></h2>' +
        '<span style="font-size: 14px; line-height: 1.5; color: var(--text-secondary);">' + escapeHtml(category) + ' · ' + escapeHtml(this.plural(group.hits.length, 'match')) + '</span>' +
        '</div>' +
        '<ul role="list" style="list-style: none; margin: 0; padding: 0; border-top: 1px solid var(--rule);">';
      for (const hit of shown) {
        const m = escapeHtml(hit.method);
        html +=
          '<li><a class="bk-hit" href="' + escapeHtml(url + '#' + hit.anchor) + '">' +
          '<span class="bk-m m-' + m + '">' + m + '</span>' +
          '<code style="font-family: var(--mono); font-size: 14px; line-height: 1.55; color: var(--heading); overflow-wrap: anywhere;">' + highlight(hit.path, hit.pathLower, needle) + '</code>' +
          '<span class="bk-hit-sum" style="font-size: 15px; line-height: 1.5; color: var(--text);">' + highlight(hit.summary, hit.summaryLower, needle) + '</span>' +
          '</a></li>';
      }
      html += '</ul></section>';
    }
    const remaining = visible.length - Math.min(visible.length, this.limit);
    if (remaining > 0) {
      html +=
        '<div><button type="button" class="bk-btn2" data-search-more style="height: 44px; padding: 0 22px; border: 1px solid var(--edge); border-radius: 999px; background: transparent; color: var(--text); font: inherit; font-size: 15px; font-weight: 700; cursor: pointer;">' +
        escapeHtml(fill(this.dataset.more, { count: String(remaining) })) +
        '</button></div>';
    }
    if (this.groupsEl) {
      this.groupsEl.innerHTML = html;
      this.groupsEl.hidden = nothing;
    }
  }
}

if (!customElements.get('bk-endpoint-search')) customElements.define('bk-endpoint-search', BkEndpointSearch);
